<template>
  <div
    v-bind="$attrs"
    :id="null"
    class="ds-textarea"
    :class="{ 'ds-textarea--light': light }"
  >
    <textarea
      v-bind="$attrs"
      ref="textarea"
      class="ds-textarea__input"
      :value="modelValue"
      :placeholder="placeholder"
      :disabled="isDisabled"
      :aria-invalid="isMarkedAsInvalid || (hasErrors && !modelValue)"
      :rows="rows"
      :maxlength="maxlength"
      :data-test-id="dataTestId"
      :data-min-rows="dataMinRows"
      :data-max-rows="dataMaxRows"
      :style="autogrow ? { resize: 'none', overflow: 'hidden' } : {}"
      @input="onInput($event.target.value)"
      @change="$emit('change', $event)"
      @blur="$emit('blur', $event)"
      @focus="$emit('focus', $event)"
    />
    <FormCaption
      v-if="caption"
      type="info"
      class="mt-1"
    >
      {{ caption }}
    </FormCaption>
    <div class="flex justify-between" v-auto-animate>
      <FormCaption
        v-if="hasErrors&&!hideErrorsCaption"
        type="invalid"
        class="mt-1"
      >
        {{ firstError }}
      </FormCaption>
      <div
        v-if="maxCharactersCounter"
        class="text-xs grow text-right whitespace-nowrap"
      >
        {{ internalModel.length }} / {{ maxCharactersCounter }}
      </div>
    </div>
  </div>
</template>

<script>
import htmlProps from '../htmlProps.js';
import FormCaption from './FormCaption.vue';
import validation from './validation-mixin';

export default {
  name: 'TextareaField',
  emits: ['change', 'update:modelValue', 'blur', 'focus'],
  mixins: [validation, htmlProps],
  components: { FormCaption },
  // Attrs land on both elements (callers rely on it, e.g. `class="resize-none"` vs `class="mt-1"`), but a duplicated
  // `id` would make `<label for>` point at the wrapper, so the wrapper drops it with `:id="null"`.
  inheritAttrs: false,
  props: {
    // eslint-disable-next-line vue/require-default-prop
    modelValue: {
      type: String,
    },
    // eslint-disable-next-line vue/require-default-prop
    placeholder: {
      type: String,
    },
    // eslint-disable-next-line vue/require-default-prop
    rows: {
      type: Number,
    },
    // eslint-disable-next-line vue/require-default-prop
    maxlength: {
      type: Number,
    },
    isDisabled: {
      type: Boolean,
      default: false,
    },
    // eslint-disable-next-line vue/require-default-prop
    caption: {
      type: String,
      required: false,
    },
    autogrow: {
      type: Boolean,
      default: false,
    },
    dataMinRows: {
      type: Number,
      default: null,
    },
    dataMaxRows: {
      type: Number,
      default: null,
    },
    maxCharactersCounter: {
      type: Number,
      default: null,
    },
    light: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      internalModel: this.modelValue,
    };
  },
  watch: {
    modelValue() {
      this.internalModel = this.modelValue;
      if (this.autogrow) {
        this.$nextTick(() => {
          this.adjustHeight();
        });
      }
    },
  },
  methods: {
    onInput(value) {
      this.$emit('update:modelValue', value);
      this.internalModel = value;

      if (this.autogrow) {
        this.adjustHeight();
      }
    },
    lineHeightPx(textarea) {
      const style = window.getComputedStyle(textarea);
      const lineHeight = parseInt(style.lineHeight, 10);
      // `line-height: normal` computes to the string "normal" (not px) in Chrome, so the row math
      // would be NaN and the min/max-rows bounds would silently stop working. Fall back to ~1.2× font.
      return Number.isNaN(lineHeight) ? Math.ceil(parseInt(style.fontSize, 10) * 1.2) : lineHeight;
    },
    adjustHeight() {
      const textarea = this.$refs.textarea;
      if (!textarea) return;

      textarea.style.height = 'auto';

      let newHeight = textarea.scrollHeight;
      const lineHeight = this.lineHeightPx(textarea);

      if (this.dataMinRows) {
        const minHeight = lineHeight * this.dataMinRows;
        newHeight = Math.max(newHeight, minHeight);
      }

      if (this.dataMaxRows) {
        const maxHeight = lineHeight * this.dataMaxRows;
        newHeight = Math.min(newHeight, maxHeight);

        if (newHeight >= maxHeight) {
          textarea.style.overflowY = 'auto';
        } else {
          textarea.style.overflowY = 'hidden';
        }
      }

      textarea.style.height = `${newHeight}px`;
    },
  },
  mounted() {
    if (this.autogrow) {
      this.$nextTick(() => {
        this.adjustHeight();
      });
    }
  },
};
</script>
